import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card } from "../../components/ui/Button";
import { Button } from "../../components/ui/Button";
import { Input, Select, Alert, Tabs } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import { tradeService } from "../../services/trades";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchWallets } from "../../store/slices/tradeSlice";
import { Transaction } from "../../types";
import { formatUSD, formatDate } from "../../utils/formatters";
import { statusBadge } from "../../components/ui/Button";
import { NETWORKS } from "../../utils/constants";
import toast from "react-hot-toast";
import { Ico } from "../../components/ui/Icon";

const schema = z.object({
  cryptocurrency: z.string().min(1),
  network: z.string().min(1),
  amount_usd: z
    .string()
    .refine((v) => !isNaN(Number(v)) && Number(v) >= 20, "Minimum $20"),
  address: z.string().min(10, "Enter a valid wallet address"),
  wallet_type: z.string(), // always 'real' — set via hidden input, demo blocked server-side
});
type FormData = z.infer<typeof schema>;

const WITHDRAW_COINS = ["BTC", "ETH", "USDT", "XRP", "SOL", "BNB"];

const Withdrawal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { wallets } = useAppSelector((s) => s.trade);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [activeTab, setActiveTab] = useState("withdraw");

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      cryptocurrency: "USDT",
      network: "TRC20",
      amount_usd: "",
      address: "",
      wallet_type: "real",
    },
  });

  const selectedCoin = watch("cryptocurrency");
  const availableNetworks = NETWORKS[selectedCoin] || ["Native"];
  const realBalance = wallets.real?.balance || 0;

  useEffect(() => {
    dispatch(fetchWallets());
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await tradeService.getTransactions({
        type: "withdrawal",
        per_page: 20,
      });
      setTransactions(res.data.data || []);
    } catch {}
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await tradeService.submitWithdrawal({
        ...data,
        amount_usd: Number(data.amount_usd),
      });
      toast.success("Withdrawal request submitted! Awaiting admin approval.");
      reset();
      dispatch(fetchWallets());
      loadHistory();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to submit withdrawal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Tabs
        tabs={[
          { id: "withdraw", label: "Withdraw", icon: <Ico name="arrow_up" /> },
          { id: "history", label: "History", count: transactions.length },
        ]}
        active={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {activeTab === "withdraw" ? (
        <Card>
          <div className="flex flex-col items-center gap-2 mb-6 pb-4 border-b border-gray-800/40">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Ico name="arrow_up" className="w-5 h-5 text-orange-400" />
            </div>
            <h3 className="font-display font-semibold text-gray-50">
              Withdraw Funds
            </h3>
          </div>

          <Alert
            type="info"
            message="Withdrawals are only available from your Real account. Demo funds are virtual and cannot be withdrawn."
            className="mb-5"
          />

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            {/* Hidden — always submits wallet_type=real */}
            <input type="hidden" {...register("wallet_type")} value="real" />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Cryptocurrency"
                options={WITHDRAW_COINS.map((c) => ({ value: c, label: c }))}
                {...register("cryptocurrency")}
              />
              <Select
                label="Network"
                options={availableNetworks.map((n: string) => ({
                  value: n,
                  label: n,
                }))}
                {...register("network")}
              />
            </div>

            <Input
              label="Amount (USD)"
              type="number"
              placeholder="Minimum $20"
              prefix={<span className="text-gray-400">$</span>}
              error={errors.amount_usd?.message}
              hint={`Available: ${formatUSD(realBalance)}`}
              {...register("amount_usd")}
            />

            <Input
              label="Withdrawal Address"
              placeholder="Your cryptocurrency wallet address"
              error={errors.address?.message}
              hint="Ensure this address is correct — withdrawals cannot be reversed"
              {...register("address")}
            />

            <Button
              type="submit"
              fullWidth
              loading={loading}
              icon={<Ico name="arrow_up" />}
            >
              Submit Withdrawal Request
            </Button>
          </form>
        </Card>
      ) : (
        <div>
          <h3 className="font-display font-semibold text-gray-50 mb-4">
            Withdrawal History
          </h3>
          <Table
            columns={[
              {
                key: "reference",
                label: "Reference",
                render: (r) => (
                  <span className="font-mono text-xs text-gray-400">
                    {r.reference}
                  </span>
                ),
              },
              {
                key: "cryptocurrency",
                label: "Asset",
                render: (r) => (
                  <span className="font-mono font-semibold">
                    {r.cryptocurrency}
                  </span>
                ),
              },
              {
                key: "amount_usd",
                label: "Amount",
                render: (r) => formatUSD(r.amount_usd || 0),
              },
              {
                key: "address",
                label: "Address",
                render: (r) => (
                  <span className="font-mono text-xs text-gray-400">
                    {r.address?.slice(0, 12)}...
                  </span>
                ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => statusBadge(r.status),
              },
              {
                key: "created_at",
                label: "Date",
                render: (r) => (
                  <span className="text-xs text-gray-400">
                    {formatDate(r.created_at)}
                  </span>
                ),
              },
            ]}
            data={transactions}
            keyExtractor={(r) => r.id}
            emptyMessage="No withdrawal history"
          />
        </div>
      )}
    </div>
  );
};

export default Withdrawal;
